import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./RunQueue.css";
import FleetStrip from "./FleetStrip";
import { AnalysisChips, PhaseChip } from "./RunLabelParts";
import {
  fleetStripModel,
  relativeTime,
  formatDateTime,
  durationBetween,
  outcomeLabel,
  formatMinutes,
} from "./runQueueFormat";
import {
  describeRun,
  queueRowTitle,
} from "../RSAAnalysis/InternalComponents/runLabel";
import { runDashboardHref } from "../../../utils/rsaRouteParams";

export const QUEUE_EXPLAINER =
  "Runs started from a project go through this queue automatically. Queued (not started) analyses wait here until an admin starts them; order decides who gets resources first when the fleet is saturated.";

export const HISTORY_PERIODS = [
  { value: "today", label: "Today" },
  { value: "week", label: "Week" },
  { value: "all", label: "All" },
];

export const STATE_HELD = "Queued, not started";
export const STATE_WAITING = "Waiting for capacity";

/** New id order after moving the entry at `from` to index `to`. */
export const moveId = (ids, from, to) => {
  const list = [...ids];
  if (from < 0 || from >= list.length || to < 0 || to >= list.length || from === to) return list;
  const [moved] = list.splice(from, 1);
  list.splice(to, 0, moved);
  return list;
};

/**
 * "Multi-project runs" view (its own left-sidebar entry; owner's model of
 * 2026-09-08 evening — the project picker is gone, runs enter the queue from
 * a project's own form: "Run" = queued + admitted automatically, "Queue"
 * (admin) = HELD until an admin starts it here).
 *
 *   Queued   held + pending entries in one table. Pending rows first (the
 *            live admission order, #1 = next), then held rows ("Queued, not
 *            started"). Admins: checkbox per held row (all selected by
 *            default), up/down arrows or drag within a group (POST
 *            /rsa/v2/queue/reorder — held rows order among held, pending among
 *            pending), Cancel, and ONE "Start selected" (POST /rsa/v2/queue/start
 *            with the selected held ids in list order). Non-admins: read-only.
 *   Running  every active entry the viewer may see — read-only for everyone.
 *   History  period Today (default) / Week / All, scope All / Mine
 *            (`history` + `onHistoryChange`; the page feeds them to useRunQueue
 *            as `since` / `mine`).
 *   Fleet    strip + gauge + policy + waiting reason — admins only.
 *
 * `runQueue` is the useRunQueue() result (already scoped to the caller's
 * project_ids for non-admins).
 */
function RunQueuePanel({
  userEmail = "",
  isAdmin = false,
  runQueue,
  history = { since: "today", mine: false },
  onHistoryChange = null,
  onOpenRun = null,
  now = () => Date.now(),
}) {
  const [dragId, setDragId] = useState(null);
  const [dragGroup, setDragGroup] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  // Held rows the admin UNCHECKED — everything else held is selected, so a
  // newly appearing held row is selected by default and a vanished one
  // (started / cancelled) needs no bookkeeping.
  const [deselectedHeld, setDeselectedHeld] = useState(() => new Set());
  const [startResult, setStartResult] = useState(null);
  const [starting, setStarting] = useState(false);

  const queue = runQueue?.queue || null;
  const fleet = runQueue?.fleet || null;
  const pending = useMemo(() => queue?.pending || [], [queue]);
  const held = useMemo(() => queue?.held || [], [queue]);
  const active = queue?.active || [];
  const done = queue?.done || [];
  const tickByRun = useMemo(() => {
    const m = new Map();
    for (const a of fleet?.last_tick?.active_runs || []) {
      if (a?.run_id) m.set(String(a.run_id), a);
    }
    return m;
  }, [fleet]);
  const gpuJobsByRun = fleet?.last_tick?.fleet?.gpu_jobs_by_run || queue?.fleet?.gpu_jobs_by_run || {};
  const model = fleetStripModel(fleet, queue);
  const policy = model.policy || {};
  const busy = runQueue?.busyIds || new Set();
  const nowMs = now();

  const pendingIds = useMemo(() => pending.map((e) => String(e.id)), [pending]);
  const heldIds = useMemo(() => held.map((e) => String(e.id)), [held]);
  const heldKey = heldIds.join(",");
  const queuedIds = useMemo(() => [...pendingIds, ...heldIds], [pendingIds, heldIds]);
  const anyQueuedBusy = queuedIds.some((id) => busy.has(id));

  // Admin-only controls: ordering, cancelling and starting. Non-admins see
  // the same table read-only (their scope is already applied by the page).
  const canReorder = isAdmin && Boolean(runQueue?.reorder);
  const canCancel = isAdmin && Boolean(runQueue?.cancel);
  const canStart = isAdmin && Boolean(runQueue?.start);

  // Forget unchecks for rows that are no longer held.
  useEffect(() => {
    setDeselectedHeld((prev) => {
      const next = new Set([...prev].filter((id) => heldIds.includes(id)));
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heldKey]);
  const isSelected = (id) => !deselectedHeld.has(id);
  const toggleSelected = (id) => {
    setStartResult(null);
    setDeselectedHeld((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const setAllSelected = (on) => {
    setStartResult(null);
    setDeselectedHeld(on ? new Set() : new Set(heldIds));
  };
  const selectedInOrder = heldIds.filter(isSelected);

  const handleStart = useCallback(async () => {
    if (!canStart || selectedInOrder.length === 0 || starting) return;
    setStarting(true);
    setStartResult(null);
    try {
      const body = await runQueue.start(selectedInOrder);
      const started = Array.isArray(body?.started) ? body.started : [];
      const ignored = Array.isArray(body?.ignored) ? body.ignored : [];
      setStartResult({
        ok: ignored.length === 0,
        message:
          `${started.length} run${started.length === 1 ? "" : "s"} started` +
          (ignored.length ? ` · ${ignored.length} no longer held` : ""),
      });
    } catch (err) {
      setStartResult({
        ok: false,
        message:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to start the selected runs",
      });
    } finally {
      setStarting(false);
    }
  }, [canStart, selectedInOrder, starting, runQueue]);

  // Explicit ordering within a group: the group's whole visible list is sent
  // back in its new order (POST /rsa/v2/queue/reorder); the backend makes that
  // order the admission order among those entries (held among held).
  const groupIds = (group) => (group === "held" ? heldIds : pendingIds);
  const reorderTo = useCallback(
    (group, ids) => {
      if (!canReorder) return;
      const current = group === "held" ? heldIds : pendingIds;
      const same = ids.length === current.length && ids.every((id, i) => id === current[i]);
      if (same) return;
      runQueue.reorder(ids);
    },
    [canReorder, heldIds, pendingIds, runQueue],
  );
  const moveEntry = (group, from, to) => reorderTo(group, moveId(groupIds(group), from, to));
  const onDragStart = (entry, group) => (e) => {
    setDragId(String(entry.id));
    setDragGroup(group);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", String(entry.id));
    } catch (err) {
      /* jsdom / older browsers without a dataTransfer */
    }
  };
  const onDragOver = (entry, group) => (e) => {
    e.preventDefault();
    if (group !== dragGroup) return;
    if (dragOverId !== String(entry.id)) setDragOverId(String(entry.id));
  };
  const onDrop = (entry, group) => (e) => {
    e.preventDefault();
    const ids = groupIds(group);
    const from = group === dragGroup ? ids.indexOf(String(dragId)) : -1;
    const to = ids.indexOf(String(entry.id));
    setDragId(null);
    setDragGroup(null);
    setDragOverId(null);
    if (from < 0 || to < 0) return;
    moveEntry(group, from, to);
  };
  const onDragEnd = () => {
    setDragId(null);
    setDragGroup(null);
    setDragOverId(null);
  };

  // Analyses as chips (label.analyses, else the entry's analysis_types).
  const renderTypes = (entry) => {
    const analyses = describeRun(null, null, entry).analyses;
    return analyses.length ? <AnalysisChips analyses={analyses} /> : "—";
  };
  const entryLabel = (entry) =>
    `${entry.project_name || `Project ${entry.project_id}`} (${entry.run_id || entry.id})`;
  // Row name per the 2026-09-08 mockup: "<Project name> · <route> · MP a–b"
  // (label) or "<Project name> · SSD, ISD" (older entries), as a LINK to the
  // run's dashboard (/projects/<id>?run=<run_id>). `onOpenRun` (the page's
  // navigate) keeps it an in-app navigation; the href still works for
  // middle-click / copy link.
  const runCell = (e) => {
    const title = queueRowTitle(e);
    const href = runDashboardHref(e.project_id, e.run_id);
    const open = (ev) => {
      if (!onOpenRun) return;
      ev.preventDefault();
      onOpenRun(e);
    };
    return (
      <td className="wrap run-queue-run-cell">
        {href ? (
          <a
            className="run-queue-run-link"
            href={href}
            onClick={open}
            title={`Open this run on the project dashboard (${e.run_id || e.id})`}
            data-testid={`run-queue-run-link-${e.id}`}
          >
            {title}
          </a>
        ) : (
          <span className="run-queue-run-link">{title}</span>
        )}
        <span className="run-queue-run-sub">
          <span className="run-queue-project-id">#{e.project_id}</span>
        </span>
      </td>
    );
  };
  // Phase cell: backend phase/phase_label/overall_percent when present;
  // otherwise (older backend) the controller tick's GPU/CPU flag — FALLBACK only.
  const renderPhase = (e) => {
    const d = describeRun(null, null, e);
    if (d.phase) {
      return (
        <PhaseChip
          phase={d.phase.phase}
          label={d.phase.label}
          percent={d.percent}
        />
      );
    }
    const tick = tickByRun.get(String(e.run_id));
    const gpuPhase = tick ? Boolean(tick.gpu_phase) : null;
    if (gpuPhase == null) return "—";
    return (
      <span className={`run-queue-phase run-queue-phase--${gpuPhase ? "gpu" : "cpu"}`}>
        {gpuPhase ? "GPU" : "CPU-only"}
      </span>
    );
  };

  const setHistory = (patch) => {
    if (onHistoryChange) onHistoryChange({ ...history, ...patch });
  };
  const periodLabel = (HISTORY_PERIODS.find((p) => p.value === history?.since) || HISTORY_PERIODS[0]).label;

  const renderQueuedRow = (e, idx, group) => {
    const id = String(e.id);
    const ids = groupIds(group);
    const label = entryLabel(e);
    const rowBusy = busy.has(e.id) || busy.has(id);
    const isHeld = group === "held";
    const classes = [
      !isHeld && e.position === 1 ? "is-head" : "",
      isHeld ? "is-held" : "",
      dragId === id ? "is-dragging" : "",
      dragOverId === id && dragId !== id ? "is-drag-over" : "",
    ]
      .filter(Boolean)
      .join(" ");
    return (
      <tr
        key={e.id}
        className={classes}
        data-testid={`run-queue-row-${e.id}`}
        data-group={group}
        draggable={canReorder && !anyQueuedBusy}
        onDragStart={canReorder ? onDragStart(e, group) : undefined}
        onDragOver={canReorder ? onDragOver(e, group) : undefined}
        onDrop={canReorder ? onDrop(e, group) : undefined}
        onDragEnd={canReorder ? onDragEnd : undefined}
      >
        {canStart && (
          <td className="run-queue-check-cell">
            {isHeld ? (
              <input
                type="checkbox"
                checked={isSelected(id)}
                onChange={() => toggleSelected(id)}
                aria-label={`Select ${label} to start`}
                disabled={rowBusy || starting}
              />
            ) : (
              ""
            )}
          </td>
        )}
        <td>{isHeld ? `H${e.position ?? idx + 1}` : `#${e.position ?? idx + 1}`}</td>
        {canReorder && (
          <td className="run-queue-order-cell">
            <span className="run-queue-order-controls">
              <i
                className="fa-solid fa-grip-vertical run-queue-drag-handle"
                aria-hidden
                title="Drag to reorder"
              />
              <button
                type="button"
                className="run-queue-order-btn"
                aria-label={`Move ${label} up`}
                title="Move up (starts earlier)"
                disabled={idx === 0 || anyQueuedBusy}
                onClick={() => moveEntry(group, idx, idx - 1)}
              >
                <i className="fa-solid fa-chevron-up" aria-hidden />
              </button>
              <button
                type="button"
                className="run-queue-order-btn"
                aria-label={`Move ${label} down`}
                title="Move down (starts later)"
                disabled={idx === ids.length - 1 || anyQueuedBusy}
                onClick={() => moveEntry(group, idx, idx + 1)}
              >
                <i className="fa-solid fa-chevron-down" aria-hidden />
              </button>
            </span>
          </td>
        )}
        {runCell(e)}
        <td className="wrap">{renderTypes(e)}</td>
        <td>
          <div>{e.user_email || "—"}</div>
          <div className="run-queue-muted">{formatDateTime(e.created_at)}</div>
        </td>
        <td>
          <span className={`run-queue-state run-queue-state--${isHeld ? "held" : "waiting"}`}>
            {isHeld ? STATE_HELD : STATE_WAITING}
          </span>
        </td>
        <td>{!isHeld && e.eta_minutes != null ? `~${formatMinutes(e.eta_minutes)}` : "—"}</td>
        {isAdmin && (
          <td className="wrap">
            {!isHeld && e.position === 1 && e.waiting_reason ? (
              <span className="run-queue-reason">{e.waiting_reason}</span>
            ) : (
              "—"
            )}
          </td>
        )}
        {canCancel && (
          <td>
            <span className="run-queue-actions">
              <button
                type="button"
                className="queued-run-action"
                disabled={rowBusy}
                onClick={() => runQueue.cancel(e)}
                aria-label={`Cancel ${label}`}
              >
                Cancel
              </button>
            </span>
          </td>
        )}
      </tr>
    );
  };

  const queuedCount = pending.length + held.length;

  return (
    <div className="run-queue-panel" data-testid="run-queue-panel">
      <div className="run-queue-header">
        <h3>Multi-project runs</h3>
        {isAdmin && <FleetStrip fleet={fleet} queue={queue} loading={runQueue?.loading} />}
        <button
          type="button"
          className="run-queue-refresh"
          onClick={() => runQueue?.refresh && runQueue.refresh()}
          disabled={!runQueue?.refresh}
          title="Refresh now (auto-refreshes every 60 s)"
        >
          <i className="fa-solid fa-rotate" aria-hidden /> Refresh
        </button>
      </div>
      <p className="run-queue-explainer" data-testid="run-queue-explainer">
        <i className="fa-solid fa-circle-info" aria-hidden /> {QUEUE_EXPLAINER}
      </p>
      {runQueue?.error && <div className="run-queue-error">{runQueue.error}</div>}

      <div className={`run-queue-grid${isAdmin ? "" : " run-queue-grid--single"}`}>
        <div className="run-queue-tile" style={{ gap: "0.875rem" }}>
          {/* ---------- (a) queued: held + pending ---------- */}
          <section aria-label="Queued runs">
            <div className="run-queue-tile-header">
              <h4>Queued ({queuedCount})</h4>
              <span className="run-queue-muted">
                {canReorder && queuedCount > 1
                  ? "Drag rows or use the arrows — the order here is the order runs start in when the fleet is saturated."
                  : "Runs start in this order when the fleet is saturated."}
                {held.length > 0 && !isAdmin ? " Queued (not started) analyses wait for an admin to start them." : ""}
              </span>
              {isAdmin && model.waitingReason && pending.length > 0 && (
                <span className="run-queue-muted">Head is waiting: {model.waitingReason}</span>
              )}
            </div>
            {canStart && held.length > 0 && (
              <div className="run-queue-submit-row" data-testid="run-queue-start-row">
                <button
                  type="button"
                  className="run-queue-submit-btn"
                  disabled={selectedInOrder.length === 0 || starting || anyQueuedBusy}
                  onClick={handleStart}
                  data-testid="run-queue-start"
                  title="Move the selected not-started analyses into the queue, in the order shown"
                >
                  {starting
                    ? "Starting…"
                    : `Start selected (${selectedInOrder.length})`}
                </button>
                <button
                  type="button"
                  className="run-queue-refresh"
                  onClick={() => setAllSelected(selectedInOrder.length < heldIds.length)}
                  disabled={starting}
                >
                  {selectedInOrder.length < heldIds.length ? "Select all" : "Select none"}
                </button>
                {startResult && (
                  <span className={`run-queue-submit-result${startResult.ok ? "" : " is-error"}`}>
                    {startResult.message}
                  </span>
                )}
              </div>
            )}
            {queuedCount === 0 ? (
              <div className="run-queue-empty">Nothing queued.</div>
            ) : (
              <div className="run-queue-table-wrap">
                <table className="run-queue-table" data-testid="run-queue-pending">
                  <thead>
                    <tr>
                      {canStart && <th className="run-queue-check-head" aria-label="Start selection" />}
                      <th>#</th>
                      {canReorder && <th className="run-queue-order-head">Order</th>}
                      <th>Run</th>
                      <th>Analyses</th>
                      <th>Submitted</th>
                      <th>State</th>
                      <th>ETA</th>
                      {isAdmin && <th>Waiting</th>}
                      {canCancel && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((e, idx) => renderQueuedRow(e, idx, "pending"))}
                    {held.map((e, idx) => renderQueuedRow(e, idx, "held"))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ---------- (b) running ---------- */}
          <section aria-label="Running runs">
            <div className="run-queue-tile-header">
              <h4>Running ({active.length})</h4>
              <span className="run-queue-muted">
                GPU phase = preprocess + analysis; CPU-only once reports have started.
              </span>
            </div>
            {active.length === 0 ? (
              <div className="run-queue-empty">No queued run is executing right now.</div>
            ) : (
              <div className="run-queue-table-wrap">
                <table className="run-queue-table" data-testid="run-queue-active">
                  <thead>
                    <tr>
                      <th>Run</th>
                      <th>Analyses</th>
                      <th>Phase</th>
                      <th>Started</th>
                      <th>Elapsed</th>
                      {/* "GPU jobs now" is fleet detail — admins only. */}
                      {isAdmin && <th>GPU jobs now</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {active.map((e) => {
                      const tick = tickByRun.get(String(e.run_id));
                      const gpuJobs = tick?.gpu_jobs ?? gpuJobsByRun[e.run_id] ?? 0;
                      return (
                        <tr key={e.id} data-testid={`run-queue-active-row-${e.id}`}>
                          {runCell(e)}
                          <td className="wrap">{renderTypes(e)}</td>
                          <td className="wrap">{renderPhase(e)}</td>
                          <td>{formatDateTime(e.admitted_at || e.created_at)}</td>
                          <td>{durationBetween(e.admitted_at, null, nowMs) || "—"}</td>
                          {isAdmin && <td data-testid={`run-queue-gpu-jobs-${e.id}`}>{gpuJobs}</td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ---------- (c) history ---------- */}
          <section aria-label="Run history">
            <div className="run-queue-tile-header">
              <h4>History ({done.length})</h4>
              <div className="run-queue-history-controls" data-testid="run-queue-history-controls">
                <span className="run-queue-seg" role="group" aria-label="Period">
                  {HISTORY_PERIODS.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      className={history?.since === p.value ? "is-on" : ""}
                      aria-pressed={history?.since === p.value}
                      onClick={() => setHistory({ since: p.value })}
                    >
                      {p.label}
                    </button>
                  ))}
                </span>
                <span className="run-queue-seg" role="group" aria-label="Scope">
                  <button
                    type="button"
                    className={!history?.mine ? "is-on" : ""}
                    aria-pressed={!history?.mine}
                    onClick={() => setHistory({ mine: false })}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={history?.mine ? "is-on" : ""}
                    aria-pressed={Boolean(history?.mine)}
                    onClick={() => setHistory({ mine: true })}
                    title={userEmail ? `Only runs submitted by ${userEmail}` : "Only your runs"}
                  >
                    Mine
                  </button>
                </span>
              </div>
            </div>
            {done.length === 0 ? (
              <div className="run-queue-empty">
                No finished queue entries {history?.mine ? "of yours " : ""}
                {periodLabel === "All" ? "yet" : `for ${periodLabel.toLowerCase() === "today" ? "today" : "this week"}`}.
              </div>
            ) : (
              <div className="run-queue-table-wrap">
                <table className="run-queue-table" data-testid="run-queue-history">
                  <thead>
                    <tr>
                      <th>Outcome</th>
                      <th>Run</th>
                      <th>Analyses</th>
                      <th>Submitted by</th>
                      <th>Queued</th>
                      <th>Waited</th>
                      <th>Ran</th>
                      <th>Finished</th>
                    </tr>
                  </thead>
                  <tbody>
                    {done.map((e) => (
                      <tr key={e.id}>
                        <td className={`run-queue-outcome--${String(e.status || "").toLowerCase()}`} title={e.error || ""}>
                          {outcomeLabel(e)}
                        </td>
                        {runCell(e)}
                        <td className="wrap">{renderTypes(e)}</td>
                        <td>{e.user_email || "—"}</td>
                        <td>{formatDateTime(e.created_at)}</td>
                        <td>{durationBetween(e.created_at, e.admitted_at || e.finished_at, nowMs) || "—"}</td>
                        <td>{e.admitted_at ? durationBetween(e.admitted_at, e.finished_at, nowMs) || "—" : "—"}</td>
                        <td>{formatDateTime(e.finished_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        {/* ---------- fleet gauge (admins only) ---------- */}
        {isAdmin && (
        <section className="run-queue-tile" aria-label="Fleet gauge" data-testid="run-queue-fleet">
          <div className="run-queue-tile-header">
            <h4>Fleet</h4>
            <span className="run-queue-muted">
              Controller tick: {model.lastTick ? relativeTime(model.lastTick, nowMs) : "never"}
            </span>
          </div>
          {!model.hasFleet && (
            <div className="run-queue-error">
              Fleet probe unavailable{model.fleetError ? `: ${model.fleetError}` : ""}.
            </div>
          )}
          <div className="run-queue-gauge">
            <div className="run-queue-gauge-stat">
              <span className="run-queue-gauge-label">GPU capacity</span>
              <span className="run-queue-gauge-value">{model.gpuCapacity}</span>
            </div>
            <div className="run-queue-gauge-stat">
              <span className="run-queue-gauge-label">GPU jobs running</span>
              <span className="run-queue-gauge-value">{model.gpuRunning}</span>
              <div className="run-queue-gauge-bar">
                <div
                  className="run-queue-gauge-bar-fill"
                  style={{
                    width: `${model.gpuCapacity ? Math.min(100, (model.gpuRunning / model.gpuCapacity) * 100) : 0}%`,
                  }}
                />
              </div>
            </div>
            <div className="run-queue-gauge-stat">
              <span className="run-queue-gauge-label">Waiting for an instance</span>
              <span className="run-queue-gauge-value">{model.gpuRunnable}</span>
            </div>
            <div className="run-queue-gauge-stat" data-testid="run-queue-gauge-launching">
              <span className="run-queue-gauge-label">Instances launching</span>
              <span className="run-queue-gauge-value">{model.instancesLaunching}</span>
            </div>
            <div
              className="run-queue-gauge-stat"
              data-testid="run-queue-gauge-slots-free"
              title={model.slotsFreeNote || "GPU slots free, counting instances that are still launching"}
            >
              <span className="run-queue-gauge-label">Slots free · incl. not yet launched</span>
              <span className="run-queue-gauge-value">{model.gpuFree}</span>
              {model.slotsFreeNote && (
                <span className="run-queue-muted">{model.slotsFreeNote}</span>
              )}
            </div>
            <div className="run-queue-gauge-stat">
              <span className="run-queue-gauge-label">Runs in GPU phase</span>
              <span className="run-queue-gauge-value">{model.gpuPhaseRuns ?? "—"}</span>
            </div>
            <div className="run-queue-gauge-stat">
              <span className="run-queue-gauge-label">Active / waiting / not started</span>
              <span className="run-queue-gauge-value">
                {model.activeRuns ?? active.length} / {model.queued ?? pending.length} / {held.length}
              </span>
            </div>
          </div>
          {model.waitingReason && (
            <div className="run-queue-waiting" title="Why the head of the queue is not admitted (verbatim from the controller)">
              <i className="fa-solid fa-hourglass-half" aria-hidden /> {model.waitingReason}
            </div>
          )}
          <h4>Policy (read-only)</h4>
          <dl className="run-queue-policy">
            <div><dt>Max runs in GPU phase</dt><dd>{policy.max_gpu_phase_runs ?? "—"}</dd></div>
            <div><dt>Min free GPUs to admit</dt><dd>{policy.min_free_gpus ?? "—"}</dd></div>
            <div><dt>Max active runs</dt><dd>{policy.max_active_runs ?? "—"}</dd></div>
            <div><dt>Per-project max active</dt><dd>{policy.per_project_max_active ?? "—"}</dd></div>
            <div><dt>Admissions per tick</dt><dd>{policy.max_admissions_per_tick ?? "—"}</dd></div>
            <div><dt>Typical GPU phase</dt><dd>{policy.typical_gpu_phase_minutes != null ? `${policy.typical_gpu_phase_minutes} min` : "—"}</dd></div>
          </dl>
        </section>
        )}
      </div>
    </div>
  );
}

export default RunQueuePanel;
